import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AdminEmptyState, AdminSectionCard } from "@/components/shell/AdminPage";
import { chartEmpty } from "@/lib/journey-overview-format";
import type { JourneyOverviewPayload } from "@/lib/journey-overview";

type Props = {
  series: JourneyOverviewPayload["series"];
  chartFrom: string;
  chartTo: string;
  /** 勾选的旅程 id；空 = 只看总量 */
  selectedJourneyIds?: number[];
};

/** ② 趋势曲线：发送人数 + 新增 + 流失（可选叠加旅程） */
export function OverviewTrendChart({
  series,
  chartFrom,
  chartTo,
  selectedJourneyIds = [],
}: Props) {
  const empty = chartEmpty(series);
  const journeyLines =
    selectedJourneyIds.length === 0
      ? []
      : (series.by_journey ?? []).filter((j) =>
          selectedJourneyIds.includes(j.journey_definition_id),
        );

  const data = series.days.map((day, i) => {
    const row: Record<string, string | number> = {
      day: day.slice(5), // MM-DD
      发送人数: series.sent_people[i] ?? 0,
      新增客户: series.new_contacts[i] ?? 0,
      流失客户: series.churned_contacts[i] ?? 0,
    };
    for (const j of journeyLines) {
      row[j.name] = j.sent_people[i] ?? 0;
    }
    return row;
  });

  const journeyColors = [ "#2563eb", "#9333ea", "#ea580c", "#0891b2" ];

  return (
    <AdminSectionCard
      title="近几日趋势"
      description={`${chartFrom} ~ ${chartTo} · 发送按人去重；新增/流失为账户客户池`}
    >
      {empty ? (
        <AdminEmptyState>
          <p className="font-medium text-foreground">近几日暂无发送与客户变动</p>
          <p className="mt-1">可先到「客户」查看联系人，或在旅程配置里启用旅程。</p>
        </AdminEmptyState>
      ) : (
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis dataKey="day" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} width={36} />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="发送人数"
                stroke="#0f766e"
                strokeWidth={2}
                dot={false}
              />
              <Line type="monotone" dataKey="新增客户" stroke="#16a34a" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="流失客户" stroke="#dc2626" strokeWidth={2} dot={false} />
              {selectedJourneyIds.length > 0
                ? journeyLines.map((j, idx) => (
                    <Line
                      key={j.journey_definition_id}
                      type="monotone"
                      dataKey={j.name}
                      stroke={journeyColors[idx % journeyColors.length]}
                      strokeWidth={1.5}
                      strokeDasharray="4 3"
                      dot={false}
                    />
                  ))
                : null}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </AdminSectionCard>
  );
}
